import React, { useCallback, useMemo, useState } from 'react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { FlexColumn } from '@/atoms/FlexContainers'
import { CheckIcon } from '@/atoms/Icons/CheckIcon'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { CopyIcon } from '@/atoms/Icons/CopyIcon'
import { Image } from '@/atoms/Image'
import { CaptionSM, EyebrowLG, HeadingXS, TitleLG } from '@/atoms/Text'
import { Modal } from '@/molecules/Modal'
import { ValidNoFeesDiscount } from '@/services/Theatrical/queries'
import { useTranslate } from '@/utils/translate/translate-client'
import { useTheatricalPromotionContext } from '@/views/TicketCheckoutViews/components/TheatricalPromotionContext/TheatricalPromotionContext'
import { PromoImage } from '@/views/TicketCheckoutViews/components/TheatricalPromotionContext/types'

interface Props {
  open: boolean
  onClose: () => void
  backgroundImage: PromoImage
  accentColor: string
  code: string
  noFeesDiscount?: ValidNoFeesDiscount
  backgroundIsDark: boolean
}

const textGradient = 'bg-gradient-to-b from-[#224778] to-[#35A2EA] bg-clip-text text-transparent'

export const DavidNoFeesModal = ({ open, onClose, code, backgroundImage, accentColor, backgroundIsDark }: Props) => {
  const { t } = useTranslate('tickets')
  const [copiedCode, setCopiedCode] = useState(false)
  const { experiments } = useTheatricalPromotionContext()

  const textColor = backgroundIsDark ? 'core-gray-100' : 'core-gray-950'

  const headingVariation = experiments?.['david-nofees-promo-2'] ?? 'CONTROL'

  const handleCopyCode = useCallback(() => {
    if (code != null) {
      navigator.clipboard.writeText(code)
      setCopiedCode(true)
    }
  }, [code])

  const headingCopy = useMemo(() => {
    if (headingVariation === 'ANIMATED') {
      return (
        <>
          <HeadingXS
            className={`relative whitespace-pre-line !font-['Angel_Royal_Loudes'] !text-[7rem] lowercase leading-[0.7] ${textGradient}`}
          >
            {t('numberOne', '#1')}
          </HeadingXS>
          <HeadingXS
            className={`relative whitespace-pre-line !font-['Angel_Royal_Loudes'] !text-[4rem] lowercase leading-[0.7] ${textGradient}`}
          >
            {t('animatedMovie', 'Animated Movie')}
          </HeadingXS>
          <HeadingXS
            className={`relative !font-['Angel_Royal_Loudes'] !text-[3.5rem] lowercase leading-[0.7] ${textGradient}`}
          >
            {t('thisChristmas', 'This Christmas')}
          </HeadingXS>
        </>
      )
    }

    if (headingVariation === 'CELEBRATE') {
      return (
        <>
          <HeadingXS
            className={`relative whitespace-pre-line !font-['Angel_Royal_Loudes'] !text-[3.5rem] lowercase leading-[0.7] ${textGradient}`}
          >
            {t('celebrate', 'Celebrate')}
          </HeadingXS>
          <HeadingXS
            className={`relative !font-['Angel_Royal_Loudes'] !text-[5.5rem] lowercase leading-[0.7] ${textGradient}`}
          >
            {t('christmas', 'Christmas')}
          </HeadingXS>
          <HeadingXS
            className={`relative !font-['Angel_Royal_Loudes'] !text-[3.5rem] lowercase leading-[0.7] ${textGradient}`}
          >
            {t('atTheMovies', 'At the Movies')}
          </HeadingXS>
        </>
      )
    }

    return (
      <>
        <HeadingXS className="relative whitespace-pre-line !font-['Angel_Royal_Loudes'] !text-[5rem] lowercase leading-[0.7] !text-[#3C246B]">
          {t('numberOneFamilyMovieLineBreak', '#1\nFamily Movie')}
        </HeadingXS>
        <HeadingXS className="relative !font-['Angel_Royal_Loudes'] !text-[4rem] lowercase leading-[0.7] !text-[#3C246B]">
          {t('inAmerica', 'In America')}
        </HeadingXS>
      </>
    )
  }, [headingVariation, t])

  return (
    <Modal
      backgroundScreenColor="black"
      isOpen={open}
      onClose={onClose}
      backgroundOverlayClassName="!bg-black/85"
      panelClasses={`bg-bottom bg-cover text-white !max-w-[380px] shadow-dark-4 rounded-3xl !p-6 !pt-10 relative text-center !overflow-visible`}
      transition="appear"
      className="z-[99999]"
    >
      <div className="absolute inset-0 overflow-hidden rounded-3xl">
        <div className="absolute inset-[-1px]">
          <Image
            src={backgroundImage.url}
            alt="Background Image"
            width={382}
            height={(382 * backgroundImage.height) / backgroundImage.width}
            className="h-full w-full rounded-3xl object-cover object-[bottom_center]"
          />
        </div>
      </div>
      <div>
        <div
          className={classNames(
            'absolute right-6 top-6 cursor-pointer rounded-full p-2',
            backgroundIsDark ? 'bg-white/10' : 'bg-black/10',
          )}
          onClick={onClose}
        >
          <CloseIcon color={backgroundIsDark ? 'white' : 'black'} size={18} />
        </div>
        <FlexColumn className="relative z-10 gap-6">
          <FlexColumn className="gap-1">{headingCopy}</FlexColumn>
          <FlexColumn className="w-fit self-center">
            <EyebrowLG color={textColor} weight={'semibold'} className="text-[0.938rem] uppercase">
              {t('usePromoCode', 'Use Promo Code')}
            </EyebrowLG>
            <div className="relative my-1 px-3 py-2 text-white" style={{ backgroundColor: accentColor }}>
              <TitleLG color="white" weight="bold" className={'relative'}>
                {code}
              </TitleLG>
            </div>
            <EyebrowLG color={textColor} weight="semibold" className="mt-0 text-[0.938rem] uppercase">
              {t('atCheckout', 'At Checkout')}
            </EyebrowLG>
          </FlexColumn>
          <FlexColumn className="items-center gap-3">
            <Button
              onClick={handleCopyCode}
              className="flex w-fit items-center gap-1"
              variant={backgroundIsDark ? 'white' : 'black'}
            >
              {copiedCode ? (
                <CheckIcon size={24} color={backgroundIsDark ? 'black' : 'white'} />
              ) : (
                <CopyIcon size={24} color={backgroundIsDark ? 'black' : 'white'} />
              )}
              {copiedCode ? t('copiedCode', 'Copied Code') : t('copyCode', 'Copy Code')}
            </Button>
            <CaptionSM italic color="core-gray-900">
              {t('availableForAngelPartneringTheaters', 'Available for Angel Partnering Theaters.')}
            </CaptionSM>
          </FlexColumn>
        </FlexColumn>
      </div>
    </Modal>
  )
}
